import { produce } from 'immer';
import { findIndex } from 'lodash/fp.js';
import { extractError } from 'app/lib/error';
import { DELETE_UNUSED_DATASETS, DELETE_VIEW } from 'app/store/entities/types.js';
import {
    CLOSE_SCHEDULE_MAIL_DIALOG,
    SET_TRANSFORMATION_EMAIL_SCHEDULE,
    GET_TRANSFORMATION_EMAIL_SCHEDULE,
    DELETE_SCHEDULE,
    PAUSE_SCHEDULE,
    SEND_EMAIL,
    SET_EXPLORATION_EMAIL_SCHEDULE,
    GET_SHARED_OUTPUTS_FOR_PROJECT,
    OPEN_SCHEDULE_EMAIL_DIALOG,
    SAVE_SHEET_SCHEDULE,
    CLOSE_SCHEDULE_SHEET_DIALOG,
    OPEN_SCHEDULE_SHEET_DIALOG,
    SYNC_NOW
} from './types.js';

export const INITIAL_EMAIL_DIALOG_STATE = {
    isOpen: false,
    isPending: false,
    mode: null,
    shareSourceType: null,
    assetId: null,
    values: {}
};

const INITIAL_SHEET_DIALOG_STATE = {
    isOpen: false,
    isPending: false,
    mode: null,
    assetId: null,
    values: {
        spreadsheetId: null,
        sheetId: null,
        trigger: null
    },
    error: null
};

export const INITIAL_STATE = {
    dialogs: {
        email: INITIAL_EMAIL_DIALOG_STATE,
        sheet: INITIAL_SHEET_DIALOG_STATE
    },
    isLoadingSchedules: false,
    schedulesBySpaceUid: {}
};

export const sharesReducer = produce((draft, action) => {
    switch (action.type) {
        case OPEN_SCHEDULE_EMAIL_DIALOG: {
            const { shareSourceType, assetId, dialogMode } = action.payload;
            const dialog = draft.dialogs.email;
            dialog.isOpen = true;
            dialog.mode = dialogMode;
            dialog.shareSourceType = shareSourceType;
            dialog.assetId = assetId;
            break;
        }

        case SEND_EMAIL.STARTED:
        case CLOSE_SCHEDULE_MAIL_DIALOG:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.SUCCEEDED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.SUCCEEDED:
            draft.dialogs.email = INITIAL_EMAIL_DIALOG_STATE;
            break;

        case SET_TRANSFORMATION_EMAIL_SCHEDULE.STARTED:
        case GET_TRANSFORMATION_EMAIL_SCHEDULE.STARTED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.STARTED:
            draft.dialogs.email.isPending = true;
            break;

        case SET_EXPLORATION_EMAIL_SCHEDULE.FAILED:
        case SET_EXPLORATION_EMAIL_SCHEDULE.REJECTED:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.FAILED:
        case SET_TRANSFORMATION_EMAIL_SCHEDULE.REJECTED:
            draft.dialogs.email.isPending = false;
            break;

        case GET_SHARED_OUTPUTS_FOR_PROJECT.STARTED:
            draft.isLoadingSchedules = true;
            break;
        case GET_SHARED_OUTPUTS_FOR_PROJECT.ENDED:
            draft.isLoadingSchedules = false;
            break;
        case GET_SHARED_OUTPUTS_FOR_PROJECT.SUCCEEDED: {
            const { spaceUid } = action.params;
            const { entries } = action.payload;
            draft.schedulesBySpaceUid[spaceUid] = entries;
            break;
        }

        case GET_TRANSFORMATION_EMAIL_SCHEDULE.SUCCEEDED: {
            const { recipients, message, schedule, subject } = action.payload;
            const { shareSourceType, assetId, dialogMode } = action.meta;
            const dialog = draft.dialogs.email;

            dialog.isOpen = true;
            dialog.isPending = false;
            dialog.shareSourceType = shareSourceType;
            dialog.assetId = assetId;
            dialog.mode = dialogMode;

            if (action.payload.recipients) {
                dialog.values = {
                    recipients,
                    message,
                    schedule,
                    subject
                };
            }

            break;
        }

        case DELETE_SCHEDULE.SUCCEEDED: {
            const { id, spaceUid } = action.meta.viewRef;
            const i = findIndex({ id }, draft.schedulesBySpaceUid[spaceUid]);

            if (i > -1) {
                draft.schedulesBySpaceUid[spaceUid].splice(i, 1);
            }

            break;
        }

        case DELETE_VIEW.SUCCEEDED:
        case DELETE_UNUSED_DATASETS.PROGRESS: {
            const { entityId, spaceUid } = action.payload.viewRef;
            const schedules = draft.schedulesBySpaceUid[spaceUid];

            if (schedules) {
                draft.schedulesBySpaceUid[spaceUid] = schedules.filter(
                    (schedule) => schedule.transformationId !== entityId
                );
            }

            break;
        }

        case PAUSE_SCHEDULE.SUCCEEDED: {
            const { projectId, pausedScheduleId } = action.meta;
            const i = findIndex({ id: pausedScheduleId }, draft.schedulesBySpaceUid[projectId]);

            if (i > -1) {
                draft.schedulesBySpaceUid[projectId][i].trigger.enabled = false;
            }

            break;
        }

        case SAVE_SHEET_SCHEDULE.SUCCEEDED:
        case CLOSE_SCHEDULE_SHEET_DIALOG:
            draft.dialogs.sheet = INITIAL_SHEET_DIALOG_STATE;
            break;

        case OPEN_SCHEDULE_SHEET_DIALOG.STARTED:
        case SAVE_SHEET_SCHEDULE.STARTED:
        case SYNC_NOW.STARTED:
            draft.dialogs.sheet.isPending = true;
            draft.dialogs.sheet.error = null;
            break;

        case SAVE_SHEET_SCHEDULE.FAILED:
        case SAVE_SHEET_SCHEDULE.REJECTED:
        case SYNC_NOW.FAILED:
            draft.dialogs.sheet.isPending = false;
            draft.dialogs.sheet.error = extractError(action.payload);
            break;

        case OPEN_SCHEDULE_SHEET_DIALOG.SUCCEEDED: {
            const { spreadsheetId, sheetId, schedule } = action.payload;
            const { assetId, dialogMode } = action.meta;
            const dialog = draft.dialogs.sheet;

            dialog.isOpen = true;
            dialog.isPending = false;
            dialog.mode = dialogMode;
            dialog.assetId = assetId;

            dialog.values = {
                sheetId,
                spreadsheetId,
                schedule
            };

            break;
        }
        case SYNC_NOW.SUCCEEDED:
            draft.dialogs.sheet.isPending = false;
            break;
        default:
            break;
    }
}, INITIAL_STATE);
